import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';

/**
 * Generate a signable RxRooster contract PDF as a Uint8Array.
 * @param {Object} opts
 * @param {string} opts.groupName
 * @param {string} opts.providerName
 * @param {string} opts.listingId
 * @param {string} opts.signatoryName
 * @param {string} opts.signatoryRole
 * @param {string} [opts.signatureImage] - base64 PNG (optional, if signing)
 * @returns {Promise<Uint8Array>} PDF bytes
 */
export async function generateEContractPdf({
  groupName = '[Group]',
  providerName = '[Provider]',
  listingId = '[Listing]',
  signatoryName = '',
  signatoryRole = '',
  signatureImage = null,
}) {
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([595, 842]); // A4 portrait
  const { width, height } = page.getSize();

  // Fonts
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Title
  page.drawText('RxRooster E-Contract', {
    x: 50,
    y: height - 60,
    size: 24,
    font: fontBold,
    color: rgb(0.11, 0.33, 0.82)
  });

  // Professional contract body

  const contractText = [
    'HEALTHCARE STAFFING AGREEMENT',
    '',
    'This Healthcare Staffing Agreement (the "Agreement") is entered into by and between:',
    `Group/Facility: ${groupName}`,
    'and',
    'RxRooster, LLC ("RxRooster")',
    '',
    `Regarding Listing Reference: ${listingId}`,
    '',
    'RECITALS',
    'WHEREAS, RxRooster is a professional staffing firm specializing in the placement of healthcare professionals; and',
    'WHEREAS, the Group/Facility is seeking to engage the services of a healthcare professional; and',
    'WHEREAS, RxRooster has introduced the Provider to the Group/Facility;',
    '',
    'NOW, THEREFORE, in consideration of the mutual covenants and promises contained herein, the parties agree as follows:',
    '',
    'ARTICLE 1: AUTHORITY TO BIND',
    '1.1 The parties represent and warrant that they have the authority to enter into this Agreement and to perform their obligations hereunder.',
    '',
    'ARTICLE 2: ENGAGEMENT & TERM',
    '2.1 The Group/Facility engages RxRooster to facilitate the introduction of the Provider.',
    '2.2 This Agreement is effective as of the date of signature and shall remain in effect for twelve (12) months from the Listing Date.',
    '',
    'ARTICLE 3: FEE OBLIGATION',
    '3.1 If the Group/Facility, or any affiliate thereof, directly or indirectly employs or contracts with the Provider within twelve (12) months of the Listing Date, the Group/Facility agrees to pay RxRooster a Placement Fee of Twenty-Five Thousand Dollars ($25,000 USD).',
    '3.2 The Placement Fee shall be due within thirty (30) days of the Provider’s start date.',
    '',
    'ARTICLE 4: REFUND POLICY',
    '4.1 If the Provider does not commence employment or contract with the Group/Facility within twelve (12) months of the Listing Date, the Group/Facility shall be entitled to a full refund of the Placement Fee.',
    '4.2 If the Provider commences employment or contract with the Group/Facility but terminates within ninety (90) days, the Group/Facility shall be entitled to a partial refund of the Placement Fee, calculated as follows:',
    '  50% refund if the Provider terminates within thirty (30) days;',
    '  25% refund if the Provider terminates within sixty (60) days;',
    '  No refund if the Provider terminates after ninety (90) days.',
    '',
    'ARTICLE 5: PAYMENT TERMS',
    '5.1 The Placement Fee shall be paid in full within thirty (30) days of the Provider’s start date.',
    '5.2 Late payments may incur interest at the lesser of 1.5% per month or the maximum rate permitted by law.',
    '',
    'ARTICLE 6: ELECTRONIC SIGNATURES',
    '6.1 The parties agree that electronic signatures shall be deemed valid and binding, and shall have the same force and effect as manual signatures.',
    '',
    'ARTICLE 7: SEVERABILITY',
    '7.1 If any provision of this Agreement is held to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.',
    '',
    'ARTICLE 8: NOTICES',
    '8.1 Any notices required or permitted to be given under this Agreement shall be in writing and shall be delivered personally, by email, or by certified mail, return receipt requested.',
    '',
    'ARTICLE 9: ASSIGNMENT',
    '9.1 This Agreement may not be assigned by either party without the prior written consent of the other party.',
    '',
    'ARTICLE 10: FORCE MAJEURE',
    '10.1 Neither party shall be liable for any failure to perform its obligations under this Agreement if such failure is caused by an event beyond the party’s reasonable control, including but not limited to natural disasters, wars, and terrorist attacks.',
    '',
    'ARTICLE 11: COUNTERPARTS',
    '11.1 This Agreement may be executed in counterparts, each of which shall be deemed an original, but all of which together shall constitute one and the same instrument.',
    '',
    'IN WITNESS WHEREOF, the parties have executed this Agreement as of the date set forth below.',
  ];
  let y = height - 100;
  contractText.forEach(line => {
    page.drawText(line, {
      x: 50,
      y,
      size: 12,
      font,
      color: rgb(0.1, 0.1, 0.1)
    });
    y -= 20;
  });

  // Signatory fields
  y -= 20;
  page.drawText('Signatory Name:', { x: 50, y, size: 12, font: fontBold });
  page.drawText(signatoryName || '________________________', { x: 180, y, size: 12, font });
  y -= 20;
  page.drawText('Signatory Role:', { x: 50, y, size: 12, font: fontBold });
  page.drawText(signatoryRole || '________________________', { x: 180, y, size: 12, font });

  // Signature box
  y -= 40;
  page.drawText('Signature:', { x: 50, y, size: 12, font: fontBold });
  page.drawRectangle({ x: 180, y: y - 4, width: 220, height: 40, borderColor: rgb(0.7,0.7,0.7), borderWidth: 1, color: rgb(1,1,1) });
  // If signatureImage is provided, embed it
  if (signatureImage) {
    const pngData = signatureImage.replace(/^data:image\/png;base64,/, '');
    const pngBytes = Uint8Array.from(atob(pngData), c => c.charCodeAt(0));
    const pngImage = await pdfDoc.embedPng(pngBytes);
    const sigDims = pngImage.scale(0.25);
    page.drawImage(pngImage, {
      x: 185,
      y: y,
      width: sigDims.width,
      height: sigDims.height
    });
  }

  // Date
  y -= 60;
  const today = new Date();
  page.drawText('Date:', { x: 50, y, size: 12, font: fontBold });
  page.drawText(today.toLocaleDateString(), { x: 180, y, size: 12, font });

  return await pdfDoc.save();
}
